import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Divider,
  IconButton,
  Paper,
  Stack,
  MenuItem,
  Chip,
  Menu,
  Avatar,
  CircularProgress
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteConfirmDialog from '../Common/DeleteConfirmDialog';
import './styles.css';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import PersonIcon from '@mui/icons-material/Person';
import CheckIcon from '@mui/icons-material/Check';
import BusinessIcon from '@mui/icons-material/Business';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { TASK_PRIORITY_OPTIONS, TASK_STATUS_OPTIONS, TASK_TYPE, getPriorityColor, getStatusColor, getPriorityLabel, getStatusLabel, getTaskTypeLabel, getTaskTypeColor } from '../../models/TaskDTO';
import { useSnackbar } from 'notistack';
import { useUserData, getUserFullName } from '../../hooks/useUserData';
import UserSearchMenu from '../Common/UserSearchMenu';
import ContactSearchMenu from '../Common/ContactSearchMenu';
import LeadDetailDialog from '../Leads/LeadDetailDialog';
import LeadContactDialog from '../Leads/LeadContactDialog';
import { Lead, LeadDTO } from '../../api/generated/requests/types.gen';
import {LeadAssignmentService, LeadTaskManagementService, LeadCrudService} from "../../api";

// Definicja typu wydarzenia
export interface CalendarEvent {
  Id: string;
  Subject: string;
  StartTime: Date;
  EndTime: Date;
  Description?: string;
  IsAllDay?: boolean;
  CategoryColor?: string;
  Status?: string;
  Priority?: 'A' | 'B' | 'C' | 'D' | 'E';
  TaskType?: 'EMAIL' | 'PHONE' | 'MEETING' | 'MESSAGE' | 'OTHER';
  assignedToUserId?: string;
  prefixId?: string;
}

interface SidePanelProps {
  selectedEvent: CalendarEvent | null;
  onClose: () => void;
  onSave: (event: CalendarEvent) => void;
  onDelete: (eventId: string) => void;
  onTaskUpdate?: (taskId: number, updates: Partial<CalendarEvent>) => Promise<void>;
  onOpenTaskDetail?: (taskId: number) => void;
}

const SidePanel: React.FC<SidePanelProps> = ({
  selectedEvent,
  onClose,
  onSave,
  onDelete,
  onTaskUpdate,
  onOpenTaskDetail
}) => {
  // Inline editing states for individual fields
  const [titleEditMode, setTitleEditMode] = useState(false);
  const [descriptionEditMode, setDescriptionEditMode] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<HTMLElement | null>(null);
  const [priorityMenuAnchor, setPriorityMenuAnchor] = useState<HTMLElement | null>(null);
  const [taskTypeMenuAnchor, setTaskTypeMenuAnchor] = useState<HTMLElement | null>(null);
  const [assignUserMenuAnchor, setAssignUserMenuAnchor] = useState<HTMLElement | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Contact assignment states
  const [assignLeadMenuAnchor, setAssignLeadMenuAnchor] = useState<HTMLElement | null>(null);
  const [assignedLeads, setAssignedLeads] = useState<Lead[]>([]);
  const [loadingAssignedLeads, setLoadingAssignedLeads] = useState(false);
  
  // Date editing states
  const [startDatePickerOpen, setStartDatePickerOpen] = useState(false);
  const [endDatePickerOpen, setEndDatePickerOpen] = useState(false);
  const [tempStartDate, setTempStartDate] = useState<Date | null>(null);
  const [tempEndDate, setTempEndDate] = useState<Date | null>(null);
  
  // Lead detail dialog states
  const [openLeadDetailDialog, setOpenLeadDetailDialog] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState<number | null>(null);
  
  // Lead contact dialog states
  const [openLeadDialog, setOpenLeadDialog] = useState(false);
  const [isSubmittingLead, setIsSubmittingLead] = useState(false);
  
  // Refs for date picker anchoring
  const startDateChipRef = useRef<HTMLDivElement>(null);
  const endDateChipRef = useRef<HTMLDivElement>(null);
  
  const { enqueueSnackbar } = useSnackbar();

  // Fetch user data using optimized hook
  const {domainUsers, isLoading: loadingUsers } = useUserData();

  // Fetch assigned leads for the task
  const fetchAssignedLeads = useCallback(async (showLoading = true) => {
    if (!selectedEvent?.Id) return;

    if (showLoading) {
      setLoadingAssignedLeads(true);
    }
    try {
      const leadsData = await LeadTaskManagementService.getApiLeadTasksTaskByTaskIdLeads({
        taskId: parseInt(selectedEvent.Id)
      });
      const leadsArray = Array.isArray(leadsData) ? leadsData : (leadsData ? [leadsData] : []);
      setAssignedLeads(leadsArray);
    } catch (error) {
      console.error('Error fetching assigned leads:', error);
      enqueueSnackbar('Nie udało się pobrać przypisanych kontaktów', { variant: 'error' });
      setAssignedLeads([]);
    } finally {
      if (showLoading) {
        setLoadingAssignedLeads(false);
      }
    }
  }, [selectedEvent?.Id, enqueueSnackbar]);

  // Initialize editing states when selectedEvent changes
  useEffect(() => {
    if (selectedEvent) {
      setEditedTitle(selectedEvent.Subject || '');
      setEditedDescription(selectedEvent.Description || '');
      fetchAssignedLeads();
    } else {
      setEditedTitle('');
      setEditedDescription('');
      setTitleEditMode(false);
      setDescriptionEditMode(false);
      setAssignedLeads([]);
    }
  }, [selectedEvent, fetchAssignedLeads]);

  // Inline editing handlers
  const handleTitleClick = () => {
    setTitleEditMode(true);
    setEditedTitle(selectedEvent?.Subject || '');
  };

  const handleSaveTitle = async () => {
    if (!selectedEvent || !editedTitle.trim()) {
      enqueueSnackbar('Tytuł jest wymagany', { variant: 'error' });
      return;
    }

    if (editedTitle.length > 255) {
      enqueueSnackbar('Tytuł nie może przekraczać 255 znaków', { variant: 'error' });
      return;
    }

    try {
      const updatedEvent = { ...selectedEvent, Subject: editedTitle };
      onSave(updatedEvent);
      setTitleEditMode(false);
      enqueueSnackbar('Tytuł został zaktualizowany', { variant: 'success' });
    } catch (error) {
      console.error('Błąd podczas zapisywania tytułu:', error);
      enqueueSnackbar('Nie udało się zapisać tytułu', { variant: 'error' });
    }
  };

  const handleCancelTitleEdit = () => {
    setTitleEditMode(false);
    setEditedTitle(selectedEvent?.Subject || '');
  };

  const handleDescriptionClick = () => {
    setDescriptionEditMode(true);
    setEditedDescription(selectedEvent?.Description || '');
  };

  const handleSaveDescription = async () => {
    if (!selectedEvent) return;

    if (editedDescription.length > 5000) {
      enqueueSnackbar('Opis nie może przekraczać 5000 znaków', { variant: 'error' });
      return;
    }

    try {
      const updatedEvent = { ...selectedEvent, Description: editedDescription };
      onSave(updatedEvent);
      setDescriptionEditMode(false);
      enqueueSnackbar('Opis został zaktualizowany', { variant: 'success' });
    } catch (error) {
      console.error('Błąd podczas zapisywania opisu:', error);
      enqueueSnackbar('Nie udało się zapisać opisu', { variant: 'error' });
    }
  };

  const handleCancelDescriptionEdit = () => {
    setDescriptionEditMode(false);
    setEditedDescription(selectedEvent?.Description || '');
  };

  // Handler functions for quick changes
  const handleQuickStatusChange = async (newStatus: string) => {
    if (!selectedEvent || !onTaskUpdate) return;

    try {
      await onTaskUpdate(parseInt(selectedEvent.Id), { Status: newStatus });
      enqueueSnackbar('Status zadania został zmieniony', { variant: 'success' });
    } catch (error) {
      console.error('Błąd podczas zmiany statusu:', error);
      enqueueSnackbar('Nie udało się zmienić statusu zadania', { variant: 'error' });
    } finally {
      setStatusMenuAnchor(null);
    }
  };

  const handleQuickPriorityChange = async (newPriority: 'A' | 'B' | 'C' | 'D' | 'E') => {
    if (!selectedEvent || !onTaskUpdate) return;

    try {
      await onTaskUpdate(parseInt(selectedEvent.Id), { Priority: newPriority });
      enqueueSnackbar('Priorytet zadania został zmieniony', { variant: 'success' });
    } catch (error) {
      console.error('Błąd podczas zmiany priorytetu:', error);
      enqueueSnackbar('Nie udało się zmienić priorytetu zadania', { variant: 'error' });
    } finally {
      setPriorityMenuAnchor(null);
    }
  };

  const handleQuickTaskTypeChange = async (newTaskType: 'EMAIL' | 'PHONE' | 'MEETING' | 'MESSAGE' | 'OTHER') => {
    if (!selectedEvent || !onTaskUpdate) return;

    try {
      await onTaskUpdate(parseInt(selectedEvent.Id), { TaskType: newTaskType });
      enqueueSnackbar('Typ zadania został zmieniony', { variant: 'success' });
    } catch (error) {
      console.error('Błąd podczas zmiany typu zadania:', error);
      enqueueSnackbar('Nie udało się zmienić typu zadania', { variant: 'error' });
    } finally {
      setTaskTypeMenuAnchor(null);
    }
  };

  const handleQuickUserAssignment = async (userId?: string) => {
    if (!selectedEvent || !onTaskUpdate) return;

    try {
      await onTaskUpdate(parseInt(selectedEvent.Id), { assignedToUserId: userId });
      enqueueSnackbar(userId ? 'Zadanie zostało przypisane' : 'Przypisanie zostało usunięte', { variant: 'success' });
    } catch (error) {
      console.error('Błąd podczas zmiany przypisania:', error);
      enqueueSnackbar('Nie udało się zmienić przypisania zadania', { variant: 'error' });
    } finally {
      setAssignUserMenuAnchor(null);
    }
  };

  // Contact assignment handlers
  const handleAssignLead = async (leadId: number) => {
    if (!selectedEvent?.Id) return;

    try {
      await LeadAssignmentService.postApiLeadTasksAssignLeadsToTask({
        requestBody: {
          taskId: parseInt(selectedEvent.Id),
          leadIds: [leadId],
          assignedByUserId: '' // You might need to get current user ID
        }
      });
      enqueueSnackbar('Kontakt został przypisany do zadania', { variant: 'success' });
      // Refresh data in background without loading spinner
      await fetchAssignedLeads(false);
    } catch (error) {
      console.error('Error assigning lead:', error);
      enqueueSnackbar('Nie udało się przypisać kontaktu do zadania', { variant: 'error' });
    } finally {
      setAssignLeadMenuAnchor(null);
    }
  };

  const handleUnassignLead = async (leadId: number) => {
    if (!selectedEvent?.Id) return;

    try {
      await LeadTaskManagementService.deleteApiLeadTasksDetachLeadsFromTask({
        requestBody: {
          taskId: parseInt(selectedEvent.Id),
          leadIds: [leadId]
        }
      });
      enqueueSnackbar('Kontakt został odłączony od zadania', { variant: 'success' });
      // Refresh data in background without loading spinner
      await fetchAssignedLeads(false);
    } catch (error) {
      console.error('Error unassigning lead:', error);
      enqueueSnackbar('Nie udało się odłączyć kontaktu od zadania', { variant: 'error' });
    }
  };

  // Handler functions for LeadContactDialog
  const handleOpenLeadDialog = () => {
    setOpenLeadDialog(true);
  };

  const handleCloseLeadDialog = () => {
    setOpenLeadDialog(false);
    // Also close the ContactSearchMenu when LeadContactDialog is closed
    setAssignLeadMenuAnchor(null);
  };

  const handleSaveLead = async (lead: LeadDTO) => {
    setIsSubmittingLead(true);
    try {
      const newLead = await LeadCrudService.postApiLeadContacts({
        requestBody: lead
      });
      enqueueSnackbar('Kontakt został utworzony', { variant: 'success' });
      handleCloseLeadDialog();

      // Automatycznie przypisz nowo utworzony kontakt do zadania
      if (newLead && newLead.id && selectedEvent?.Id) {
        try {
          await LeadAssignmentService.postApiLeadTasksAssignLeadsToTask({
            requestBody: {
              taskId: parseInt(selectedEvent.Id),
              leadIds: [newLead.id],
              assignedByUserId: '' // You might need to get current user ID
            }
          });
          
          // Odśwież listę przypisanych kontaktów
          await fetchAssignedLeads();
          enqueueSnackbar('Kontakt został automatycznie przypisany do zadania', { variant: 'success' });
        } catch (assignError) {
          console.error('Błąd podczas automatycznego przypisywania kontaktu:', assignError);
          enqueueSnackbar('Kontakt został utworzony, ale nie udało się go automatycznie przypisać', { variant: 'warning' });
        }
      }

      // Wyślij globalne zdarzenie do odświeżenia listy leadów
      window.dispatchEvent(new CustomEvent('refresh-leads'));

      return newLead;
    } catch (error) {
      console.error('Błąd podczas tworzenia kontaktu:', error);
      enqueueSnackbar('Wystąpił błąd podczas tworzenia kontaktu', { variant: 'error' });
    } finally {
      setIsSubmittingLead(false);
    }
  };

  // Date handling functions
  const handleStartDateChange = (date: Date | null) => {
    setTempStartDate(date);
  };

  const handleStartDateAccept = async (date: Date | null) => {
    if (!date || !selectedEvent) return;

    const isMeeting = selectedEvent.TaskType === 'MEETING';

    try {
      let updatedEvent;
      
      // For non-meeting types, automatically calculate endDate as startDate + 15 minutes
      if (!isMeeting) {
        const autoEndDate = new Date(date);
        autoEndDate.setMinutes(autoEndDate.getMinutes() + 15);
        updatedEvent = { ...selectedEvent, StartTime: date, EndTime: autoEndDate };
        enqueueSnackbar('Termin został zaktualizowany', { variant: 'success' });
      } else {
        // For meeting types, keep the original logic
        updatedEvent = { ...selectedEvent, StartTime: date };
        enqueueSnackbar('Data rozpoczęcia została zaktualizowana', { variant: 'success' });
      }
      
      onSave(updatedEvent);
    } catch (error) {
      console.error('Error updating start date:', error);
      enqueueSnackbar('Nie udało się zaktualizować daty rozpoczęcia', { variant: 'error' });
    } finally {
      setStartDatePickerOpen(false);
      setTempStartDate(null);
    }
  };

  const handleEndDateChange = (date: Date | null) => {
    setTempEndDate(date);
  };

  const handleEndDateAccept = async (date: Date | null) => {
    if (!date || !selectedEvent) return;

    // Validate that end date is not earlier than start date
    if (date < new Date(selectedEvent.StartTime)) {
      enqueueSnackbar('Termin do nie może być wcześniejszy niż termin od', { variant: 'error' });
      return;
    }

    try {
      const updatedEvent = { ...selectedEvent, EndTime: date };
      onSave(updatedEvent);
      enqueueSnackbar('Data zakończenia została zaktualizowana', { variant: 'success' });
    } catch (error) {
      console.error('Error updating end date:', error);
      enqueueSnackbar('Nie udało się zaktualizować daty zakończenia', { variant: 'error' });
    } finally {
      setEndDatePickerOpen(false);
      setTempEndDate(null);
    }
  };

  // Helper functions
  const getUserFullNameById = (userId?: string) => {
    if (!userId) return 'Nieprzypisane';
    const user = domainUsers.find(u => u.id === userId);
    return getUserFullName(user);
  };

  const getUserInitials = (userId?: string) => {
    if (!userId) return '';
    const user = domainUsers.find(u => u.id === userId);
    if (!user) return '';
    const firstName = user.firstName || '';
    const lastName = user.lastName || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };



  if (!selectedEvent) {
    return null;
  }


  const handleDelete = () => {
    setConfirmDeleteOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedEvent) {
      setIsDeleting(true);
      try {
        onDelete(selectedEvent.Id);
      } finally {
        setIsDeleting(false);
        setConfirmDeleteOpen(false);
      }
    }
  };


  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        height: '100%',
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflowY: 'auto',
        backgroundColor: '#fafafa',
        borderRadius: 1.5,
        border: '1px solid rgba(0, 0, 0, 0.08)',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
      }}
    >
      {/* Nagłówek z przyciskiem zamknięcia */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography 
          variant="h6" 
          sx={{ 
            fontSize: '1rem', 
            fontWeight: 600,
            color: '#1a1a1a',
            letterSpacing: '-0.01em'
          }}
        >
          Zadanie ID {selectedEvent.prefixId && selectedEvent.Id ? `${selectedEvent.prefixId}-${selectedEvent.Id}` : selectedEvent.Id}
        </Typography>
        <IconButton 
          onClick={onClose} 
          size="small"
          sx={{
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.08)',
              transform: 'scale(1.05)'
            },
            transition: 'all 0.2s ease-in-out',
            borderRadius: 1.5
          }}
        >
          <CloseIcon sx={{ fontSize: '1.1rem' }} />
        </IconButton>
      </Box>

      <Divider sx={{ mb: 2, borderColor: 'rgba(0, 0, 0, 0.06)' }} />

      {/* Formularz wyświetlania/edycji */}
      <Stack spacing={1.5}>
        {/* Title Section with inline editing */}
        {(
            // Tryb podglądu
            <>
              {/* Status, Priority, Task Type in horizontal layout */}
              <Box sx={{display: 'flex', gap: 2, mb: 1, flexWrap: 'wrap'}}>
                {selectedEvent.Status && (
                    <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '100px'}}>
                      <Typography variant="caption" sx={{fontSize: '0.7rem', mb: 0.5}}>
                        Status
                      </Typography>
                      <Chip
                          label={getStatusLabel(selectedEvent.Status)}
                          size="small"
                          sx={{
                            backgroundColor: getStatusColor(selectedEvent.Status),
                            color: 'white',
                            cursor: onTaskUpdate ? 'pointer' : 'default',
                            fontSize: '0.75rem',
                            height: '24px',
                            '&:hover': onTaskUpdate ? {
                              transform: 'translateY(-1px)',
                              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)'
                            } : {},
                            transition: 'all 0.2s ease-in-out'
                          }}
                          onClick={onTaskUpdate ? (e) => setStatusMenuAnchor(e.currentTarget) : undefined}
                      />
                    </Box>
                )}

                {selectedEvent.Priority && (
                    <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '100px'}}>
                      <Typography variant="caption" sx={{fontSize: '0.7rem', mb: 0.5}}>
                        Priorytet
                      </Typography>
                      <Chip
                          label={getPriorityLabel(selectedEvent.Priority)}
                          size="small"
                          sx={{
                            alignItems: 'center',
                            backgroundColor: getPriorityColor(selectedEvent.Priority),
                            color: 'white',
                            cursor: onTaskUpdate ? 'pointer' : 'default',
                            fontSize: '0.75rem',
                            height: '24px',
                            '&:hover': onTaskUpdate ? {
                              transform: 'translateY(-1px)',
                              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)'
                            } : {},
                            transition: 'all 0.2s ease-in-out'
                          }}
                          onClick={onTaskUpdate ? (e) => setPriorityMenuAnchor(e.currentTarget) : undefined}
                      />
                    </Box>
                )}

                {selectedEvent.TaskType && (
                    <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '100px'}}>
                      <Typography variant="caption" sx={{fontSize: '0.7rem', mb: 0.5}}>
                        Typ zadania
                      </Typography>
                      <Chip
                          label={getTaskTypeLabel(selectedEvent.TaskType)}
                          size="small"
                          sx={{
                            backgroundColor: getTaskTypeColor(selectedEvent.TaskType),
                            color: 'white',
                            cursor: onTaskUpdate ? 'pointer' : 'default',
                            fontSize: '0.75rem',
                            height: '24px',
                            '&:hover': onTaskUpdate ? {
                              transform: 'translateY(-1px)',
                              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)'
                            } : {},
                            transition: 'all 0.2s ease-in-out'
                          }}
                          onClick={onTaskUpdate ? (e) => setTaskTypeMenuAnchor(e.currentTarget) : undefined}
                      />
                    </Box>
                )}
              </Box>

              {/* Dates - conditional display based on task type */}
              <Box sx={{display: 'flex', gap: 2, mb: 1, flexWrap: 'wrap', alignItems: 'center'}}>
                {selectedEvent.TaskType === 'MEETING' ? (
                  <>
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      flex: 1,
                      minWidth: '150px'
                    }}>
                      <Typography variant="caption" sx={{fontSize: '0.7rem', mb: 0.5}}>
                        Termin od
                      </Typography>
                      <Chip
                          ref={startDateChipRef}
                          label={format(new Date(selectedEvent.StartTime), 'dd MMM yyyy HH:mm', {locale: pl})}
                          variant="outlined"
                          size="small"
                          sx={{
                            cursor: 'pointer',
                            fontSize: '0.75rem',
                            height: '24px',
                            borderRadius: '12px',
                            '&:hover': {backgroundColor: 'rgba(0, 0, 0, 0.04)'}
                          }}
                          onClick={() => setStartDatePickerOpen(true)}
                      />
                    </Box>
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      flex: 1,
                      minWidth: '150px'
                    }}>
                      <Typography variant="caption" sx={{fontSize: '0.7rem', mb: 0.5, alignItems: 'center'}}>
                        Termin do
                      </Typography>
                      <Chip
                          ref={endDateChipRef}
                          label={format(new Date(selectedEvent.EndTime), 'dd MMM yyyy HH:mm', {locale: pl})}
                          variant="outlined"
                          size="small"
                          sx={{
                            cursor: 'pointer',
                            fontSize: '0.75rem',
                            height: '24px',
                            borderRadius: '12px',
                            '&:hover': {backgroundColor: 'rgba(0, 0, 0, 0.04)'}
                          }}
                          onClick={() => setEndDatePickerOpen(true)}
                      />
                    </Box>
                  </>
                ) : (
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    flex: 1,
                    minWidth: '150px'
                  }}>
                    <Typography variant="caption" sx={{fontSize: '0.7rem', mb: 0.5}}>
                      Termin
                    </Typography>
                    <Chip
                        ref={startDateChipRef}
                        label={format(new Date(selectedEvent.StartTime), 'dd MMM yyyy HH:mm', {locale: pl})}
                        variant="outlined"
                        size="small"
                        sx={{
                          cursor: 'pointer',
                          fontSize: '0.75rem',
                          height: '24px',
                          borderRadius: '12px',
                          '&:hover': {backgroundColor: 'rgba(0, 0, 0, 0.04)'}
                        }}
                        onClick={() => setStartDatePickerOpen(true)}
                    />
                  </Box>
                )}
              </Box>

              {/* Assigned Person */}
              <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mb: 1}}>
                <Typography variant="caption" sx={{fontSize: '0.7rem', mb: 0.5}}>
                  Przypisane do użytkownika
                </Typography>
                <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      cursor: onTaskUpdate ? 'pointer' : 'default',
                      border: '1px solid rgba(0, 0, 0, 0.23)',
                      borderRadius: '16px',
                      padding: '2px 8px',
                      height: '24px',
                      '&:hover': onTaskUpdate ? {backgroundColor: 'rgba(0, 0, 0, 0.04)'} : {}
                    }}
                    onClick={onTaskUpdate ? (e) => setAssignUserMenuAnchor(e.currentTarget) : undefined}
                >
                  <Avatar
                      sx={{
                        width: 18,
                        height: 18,
                        bgcolor: selectedEvent.assignedToUserId ? 'primary.main' : 'grey.300',
                        fontSize: '0.65rem'
                      }}
                  >
                    {selectedEvent.assignedToUserId ? getUserInitials(selectedEvent.assignedToUserId) :
                        <PersonIcon sx={{fontSize: '0.65rem'}}/>}
                  </Avatar>
                  <Typography variant="body2" sx={{fontSize: '0.75rem'}}>
                    {getUserFullNameById(selectedEvent.assignedToUserId)}
                  </Typography>
                </Box>
              </Box>

              {titleEditMode ? (
                  <Box sx={{display: 'flex', alignItems: 'center', mb: 1}}>
                    <TextField
                        fullWidth
                        value={editedTitle}
                        onChange={(e) => setEditedTitle(e.target.value)}
                        variant="outlined"
                        size="small"
                        label="Tytuł zadania"
                        autoFocus
                        sx={{mr: 1, '& .MuiInputBase-input': {fontSize: '0.85rem'}}}
                        InputLabelProps={{
                          sx: {bgcolor: 'white', px: 0.5}
                        }}
                        inputProps={{maxLength: 255}}
                        helperText={
                          <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                            <span>Enter - zapisz, Esc - anuluj</span>
                            <span style={{
                              color: editedTitle.length >= 255 ? '#d32f2f' : '#666',
                              fontSize: '0.85rem'
                            }}>
                        {editedTitle.length}/255
                      </span>
                          </Box>
                        }
                        error={editedTitle.length > 255}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleSaveTitle();
                          } else if (e.key === 'Escape') {
                            handleCancelTitleEdit();
                          }
                        }}
                    />
                    <IconButton onClick={handleSaveTitle} color="primary" size="small">
                      <SaveIcon fontSize="small"/>
                    </IconButton>
                    <IconButton onClick={handleCancelTitleEdit} color="default" size="small">
                      <CancelIcon fontSize="small"/>
                    </IconButton>
                  </Box>
              ) : (
                  <TextField
                      fullWidth
                      size="small"
                      margin="none"
                      value={selectedEvent.Subject}
                      variant="outlined"
                      label="Tytuł zadania"
                      InputLabelProps={{
                        sx: {bgcolor: 'white', px: 0.5}
                      }}
                      InputProps={{
                        readOnly: true,
                        endAdornment: <EditIcon fontSize="small" sx={{opacity: 0.5, cursor: 'pointer'}}/>,
                        sx: {fontSize: '0.85rem', cursor: 'pointer'}
                      }}
                      onClick={handleTitleClick}
                      sx={{mb: 1}}
                  />
              )}

              {descriptionEditMode ? (
                  <Box sx={{display: 'flex', alignItems: 'center', mt: 2}}>
                    <TextField
                        fullWidth
                        multiline
                        rows={5}
                        value={editedDescription}
                        onChange={(e) => setEditedDescription(e.target.value)}
                        variant="outlined"
                        label="Opis zadania"
                        autoFocus
                        InputLabelProps={{
                          sx: {bgcolor: 'white', px: 0.5}
                        }}
                        inputProps={{maxLength: 5000}}
                        sx={{mr: 1, '& .MuiInputBase-input': {fontSize: '0.85rem'}}}
                        helperText={
                          <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                            <span>Ctrl+Enter - zapisz, Esc - anuluj</span>
                            <span style={{
                              color: editedDescription.length >= 5000 ? '#d32f2f' : '#666',
                              fontSize: '0.85rem'
                            }}>
                        {editedDescription.length}/5000
                      </span>
                          </Box>
                        }
                        error={editedDescription.length > 5000}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && e.ctrlKey) {
                            handleSaveDescription();
                          } else if (e.key === 'Escape') {
                            handleCancelDescriptionEdit();
                          }
                        }}
                    />
                    <IconButton onClick={handleSaveDescription} color="primary" size="small">
                      <SaveIcon fontSize="small"/>
                    </IconButton>
                    <IconButton onClick={handleCancelDescriptionEdit} color="default" size="small">
                      <CancelIcon fontSize="small"/>
                    </IconButton>
                  </Box>
              ) : (
                  <TextField
                      fullWidth
                      size="small"
                      margin="none"
                      value={selectedEvent.Description || 'Brak opisu'}
                      variant="outlined"
                      label="Opis zadania"
                      InputLabelProps={{
                        sx: {bgcolor: 'white', px: 0.5}
                      }}
                      multiline
                      rows={5}
                      InputProps={{
                        readOnly: true,
                        endAdornment: <EditIcon fontSize="small" sx={{opacity: 0.5, cursor: 'pointer'}}/>,
                        sx: {fontSize: '0.85rem', cursor: 'pointer', whiteSpace: 'pre-wrap'}
                      }}
                      onClick={handleDescriptionClick}
                      sx={{mt: 2}}
                  />
              )}

              {/* Assigned Contacts */}
              <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mb: 1}}>
                <Typography variant="caption" sx={{fontSize: '0.7rem', mb: 0.5}}>
                  Przypisane kontakty
                </Typography>
                <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center'}}>
                  {loadingAssignedLeads ? (
                      <CircularProgress size={16}/>
                  ) : assignedLeads.length === 0 ? (
                      <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            border: '1px solid rgba(0, 0, 0, 0.23)',
                            borderRadius: '16px',
                            padding: '6px 12px',
                            height: '42px',
                            fontSize: '0.75rem',
                            '&:hover': {backgroundColor: 'rgba(0, 0, 0, 0.04)'}
                          }}
                          onClick={(e) => setAssignLeadMenuAnchor(e.currentTarget)}
                      >
                        <Typography variant="body2" sx={{fontSize: '0.75rem'}}>
                          Brak przypisania do kontaktu
                        </Typography>
                      </Box>
                  ) : (
                      assignedLeads.map((lead) => (
                          <Box
                              key={lead.id}
                              sx={{
                                position: 'relative',
                                display: 'inline-block',
                                '&:hover .remove-button': {
                                  opacity: 1
                                }
                              }}
                          >
                            <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  cursor: 'pointer',
                                  border: '1px solid rgba(0, 0, 0, 0.23)',
                                  borderRadius: '16px',
                                  padding: '6px 12px',
                                  height: '42px',
                                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                  '&:hover': {backgroundColor: 'rgba(25, 118, 210, 0.12)'}
                                }}
                                onClick={() => {
                                  if (lead.id) {
                                    setSelectedLeadId(lead.id);
                                    setOpenLeadDetailDialog(true);
                                  }
                                }}
                            >
                              <BusinessIcon sx={{fontSize: '0.65rem', color: 'primary.main'}}/>
                              <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start'}}>
                                <Typography variant="body2" sx={{fontSize: '0.75rem', lineHeight: 1.2}}>
                                  {lead.firstName} {lead.lastName}
                                </Typography>
                                {lead.email && (
                                    <Typography variant="body2"
                                                sx={{fontSize: '0.65rem', color: 'text.secondary', lineHeight: 1.1}}>
                                      {lead.email}
                                    </Typography>
                                )}
                                {lead.phone && (
                                    <Typography variant="body2"
                                                sx={{fontSize: '0.65rem', color: 'text.secondary', lineHeight: 1.1}}>
                                      {lead.phone}
                                    </Typography>
                                )}
                              </Box>
                            </Box>
                            <Box
                                className="remove-button"
                                sx={{
                                  position: 'absolute',
                                  top: -4,
                                  right: -4,
                                  width: 16,
                                  height: 16,
                                  borderRadius: '50%',
                                  backgroundColor: 'error.main',
                                  color: 'white',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'pointer',
                                  opacity: 0,
                                  transition: 'opacity 0.2s',
                                  '&:hover': {
                                    backgroundColor: 'error.dark'
                                  }
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (lead.id) {
                                    handleUnassignLead(lead.id);
                                  }
                                }}
                            >
                              <RemoveIcon sx={{fontSize: '0.7rem'}}/>
                            </Box>
                          </Box>
                      ))
                  )}

                  {/* Add Contact Button */}
                  <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        border: '1px solid rgba(0, 0, 0, 0.23)',
                        borderRadius: '16px',
                        padding: '6px 12px',
                        height: '42px',
                        minWidth: '40px',
                        '&:hover': {backgroundColor: 'rgba(25, 118, 210, 0.04)'}
                      }}
                      onClick={(e) => setAssignLeadMenuAnchor(e.currentTarget)}
                  >
                    <AddIcon sx={{fontSize: '0.8rem'}}/>
                  </Box>
                </Box>
              </Box>

              <Box
                  display="flex"
                  justifyContent="space-between"
                  mt={4}
                  sx={{
                    pt: 2,
                    borderTop: '1px solid rgba(0, 0, 0, 0.06)'
                  }}
              >
                <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={<DeleteIcon/>}
                    onClick={handleDelete}
                    sx={{
                      borderRadius: 2,
                      px: 1.5,
                      py: 0.5,
                      fontWeight: 600,
                      textTransform: 'none',
                      fontSize: '0.8rem',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 8px rgba(244, 67, 54, 0.25)'
                      },
                      transition: 'all 0.2s ease-in-out'
                    }}
                >
                  Usuń
                </Button>
                <Box sx={{display: 'flex', gap: 1.5}}>
                  {onOpenTaskDetail && (
                      <Button
                          variant="outlined"
                          size="small"
                          onClick={() => onOpenTaskDetail(parseInt(selectedEvent.Id))}
                          sx={{
                            borderRadius: 2,
                            px: 1.5,
                            py: 0.5,
                            fontWeight: 600,
                            textTransform: 'none',
                            fontSize: '0.8rem',
                            borderColor: 'rgba(0, 0, 0, 0.12)',
                            color: '#2c2c2c',
                            '&:hover': {
                              transform: 'translateY(-1px)',
                              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                              borderColor: 'rgba(0, 0, 0, 0.2)'
                            },
                            transition: 'all 0.2s ease-in-out'
                          }}
                      >
                        Szczegóły
                      </Button>
                  )}
                </Box>
              </Box>
            </>
        )}
      </Stack>

      {/* Status Menu */}
      <Menu
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor)}
        onClose={() => setStatusMenuAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        {TASK_STATUS_OPTIONS.map((option) => {
          const isCurrentStatus = selectedEvent?.Status === option.value;
          return (
            <MenuItem 
              key={option.value} 
              onClick={() => handleQuickStatusChange(option.value)}
              sx={{
                backgroundColor: isCurrentStatus ? 'rgba(0, 0, 0, 0.08)' : 'inherit',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  backgroundColor: getStatusColor(option.value),
                  borderRadius: '50%'
                }}
              />
              <Typography>{option.label}</Typography>
              {isCurrentStatus && <CheckIcon fontSize="small" sx={{ ml: 1 }} />}
            </MenuItem>
          );
        })}
      </Menu>

      {/* Priority Menu */}
      <Menu
        anchorEl={priorityMenuAnchor}
        open={Boolean(priorityMenuAnchor)}
        onClose={() => setPriorityMenuAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        {TASK_PRIORITY_OPTIONS.map((option) => {
          const isCurrentPriority = selectedEvent?.Priority === option.value;
          return (
            <MenuItem 
              key={option.value} 
              onClick={() => handleQuickPriorityChange(option.value as 'A' | 'B' | 'C' | 'D' | 'E')}
              sx={{
                backgroundColor: isCurrentPriority ? 'rgba(0, 0, 0, 0.08)' : 'inherit',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  backgroundColor: getPriorityColor(option.value),
                  borderRadius: '50%'
                }}
              />
              <Typography>{option.label}</Typography>
              {isCurrentPriority && <CheckIcon fontSize="small" sx={{ ml: 1 }} />}
            </MenuItem>
          );
        })}
      </Menu>

      {/* Task Type Menu */}
      <Menu
        anchorEl={taskTypeMenuAnchor}
        open={Boolean(taskTypeMenuAnchor)}
        onClose={() => setTaskTypeMenuAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        {TASK_TYPE.map((option) => {
          const isCurrentTaskType = selectedEvent?.TaskType === option.value;
          return (
            <MenuItem 
              key={option.value} 
              onClick={() => handleQuickTaskTypeChange(option.value as 'EMAIL' | 'PHONE' | 'MEETING' | 'MESSAGE' | 'OTHER')}
              sx={{
                backgroundColor: isCurrentTaskType ? 'rgba(0, 0, 0, 0.08)' : 'inherit',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  backgroundColor: getTaskTypeColor(option.value),
                  borderRadius: '50%'
                }}
              />
              <Typography>{option.label}</Typography>
              {isCurrentTaskType && <CheckIcon fontSize="small" sx={{ ml: 1 }} />}
            </MenuItem>
          );
        })}
      </Menu>

      {/* User Assignment Menu */}
      <UserSearchMenu
        anchorEl={assignUserMenuAnchor}
        open={Boolean(assignUserMenuAnchor)}
        onClose={() => setAssignUserMenuAnchor(null)}
        onSelectUser={(userId) => handleQuickUserAssignment(userId)}
        users={domainUsers}
        isLoading={loadingUsers}
        currentUserId={selectedEvent?.assignedToUserId}
        getUserDisplayName={getUserFullNameById}
      />

      {/* Contact Search Menu */}
      <ContactSearchMenu
        anchorEl={assignLeadMenuAnchor}
        open={Boolean(assignLeadMenuAnchor)}
        onClose={() => setAssignLeadMenuAnchor(null)}
        onSelectContact={(leadId) => {
          if (leadId === undefined) {
            // For the new system, we need a leadId to unassign
            return;
          } else {
            handleAssignLead(leadId);
          }
        }}
        currentContactId={undefined}
        onAddNewContact={handleOpenLeadDialog}
      />


      {/* Hidden DateTimePickers that open directly */}
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={pl}>
        <Box sx={{ position: 'absolute', opacity: 0, pointerEvents: 'none', zIndex: -1 }}>
          <DateTimePicker
            label="Termin od"
            value={tempStartDate || (selectedEvent?.StartTime ? new Date(selectedEvent.StartTime) : new Date())}
            onChange={handleStartDateChange}
            onAccept={handleStartDateAccept}
            open={startDatePickerOpen}
            onClose={() => {
              setStartDatePickerOpen(false);
              setTempStartDate(null);
            }}
            slotProps={{
              textField: {
                size: 'small'
              },
              popper: {
                anchorEl: startDateChipRef.current,
                placement: 'bottom-start',
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [0, 8],
                    },
                  },
                ],
                sx: {
                  '& .MuiPaper-root': {
                    transform: 'scale(0.85)',
                    transformOrigin: 'top left'
                  }
                }
              }
            }}
          />
          <DateTimePicker
            label="Termin do"
            value={tempEndDate || (selectedEvent?.EndTime ? new Date(selectedEvent.EndTime) : new Date())}
            onChange={handleEndDateChange}
            onAccept={handleEndDateAccept}
            minDate={selectedEvent?.StartTime ? new Date(selectedEvent.StartTime) : new Date()}
            open={endDatePickerOpen}
            onClose={() => {
              setEndDatePickerOpen(false);
              setTempEndDate(null);
            }}
            slotProps={{
              textField: {
                size: 'small'
              },
              popper: {
                anchorEl: endDateChipRef.current,
                placement: 'bottom-start',
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [0, 8],
                    },
                  },
                ],
                sx: {
                  '& .MuiPaper-root': {
                    transform: 'scale(0.85)',
                    transformOrigin: 'top left'
                  }
                }
              }
            }}
          />
        </Box>
      </LocalizationProvider>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Potwierdź usunięcie"
        content={`Czy na pewno chcesz usunąć wydarzenie: ${selectedEvent?.Subject}?`}
        isSubmitting={isDeleting}
      />

      {/* Lead Contact Dialog */}
      <LeadContactDialog
        open={openLeadDialog}
        onClose={handleCloseLeadDialog}
        lead={null}
        onSave={handleSaveLead}
        isSubmitting={isSubmittingLead}
      />

      {/* Lead Detail Dialog */}
      <LeadDetailDialog
        open={openLeadDetailDialog}
        leadId={selectedLeadId}
        onClose={() => {
          setOpenLeadDetailDialog(false);
          setSelectedLeadId(null);
        }}
        initialTab={0}
      />
    </Paper>
  );
};

export default SidePanel;
